/**
 * OptimizerSummaryPanel -- read-only view of the walk-forward optimizer's last persisted out-of-sample summary (Testing page, 2026-10-07).
 *
 * Binds to GET /api/backtest/recent -> system_config key `backtest_recent_results`, written by `python -m src.backtest.optimizer --persist`
 * (shape: build_summary() in src/backtest/optimizer.py). The caveat banner is always shown above the numbers, and losing symbols are shown as they are.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertTriangle } from 'lucide-react'
import { backtestRecent } from '../lib/api'

const mono = { fontFamily: 'JetBrains Mono, monospace' }
const card = { background: '#111827', border: '1px solid #1f2937', borderRadius: '0.75rem' }
export const CAVEAT = 'CAVEAT: Data-health checks D5/D7 were RED during this optimization window.'

const pct = (v, d = 3) => (v == null || Number.isNaN(Number(v)) ? '-' : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(d)}%`)

export function parseSummary(resp) {
  const d = resp?.data
  if (!d || typeof d !== 'object') return null
  return {
    label: d.label || '',
    generatedAt: d.generated_at || resp?.updated_at || null,
    header: d.header || '',
    pooled: d.pooled_oos || {},
    perSymbol: Object.entries(d.per_symbol_oos || {}).map(([symbol, m]) => ({ symbol, ...(m || {}) })),
    waves: Array.isArray(d.waves) ? d.waves : [],
    totalTested: d.total_tested ?? null,
    combosPerWave: d.combos_per_wave ?? null,
    sessions: d.sessions ?? null,
    floor100: !!d.meets_100_trade_floor,
    note: d.note || '',
  }
}

export default function OptimizerSummaryPanel() {
  const [view, setView] = useState(undefined)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      setView(parseSummary(await backtestRecent.get()))
    } catch (e) {
      setErr(e?.message || 'failed to load')
      setView(null)
    }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.4)' }}>
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-200 font-semibold">{CAVEAT}</p>
      </div>
      <div className="p-4" style={card}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold tracking-widest" style={{ fontFamily: 'Rajdhani, sans-serif' }}>WALK-FORWARD OPTIMIZER · LAST OUT-OF-SAMPLE RUN</h3>
          <button onClick={load} className="text-slate-400 hover:text-slate-200" aria-label="Refresh optimizer summary"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {err && <p className="text-xs text-red-400">Could not load the summary: {err}</p>}
        {view === undefined && !err && <p className="text-xs text-slate-500">Loading...</p>}
        {view === null && !err && <p className="text-xs text-slate-500">No run persisted yet. Run the optimizer with --persist to fill this panel.</p>}
        {view && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500" style={mono}>
              {view.label} · generated {view.generatedAt ? String(view.generatedAt).slice(0, 19) : '-'} · {view.sessions ?? '-'} sessions ·
              {' '}{view.totalTested != null ? Number(view.totalTested).toLocaleString() : '-'} combinations tested ({view.combosPerWave ?? '-'} per wave; the winner is the best of them)
            </p>
            <div className="rounded-lg p-3" style={{ background: '#0b1220', border: '1px solid #1f2937' }}>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Stitched pooled out-of-sample</p>
              <p className="text-sm text-slate-200 mt-1" style={mono}>
                n={view.pooled.n ?? '-'} · win {view.pooled.win_pct != null ? Number(view.pooled.win_pct).toFixed(1) : '-'}% · expectancy {pct(view.pooled.expectancy, 4)} per trade
                · 100-trade family floor {view.floor100 ? 'met' : 'NOT met'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Per symbol (out of sample)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {view.perSymbol.map((s) => {
                  const neg = Number(s.expectancy) < 0
                  return (
                    <div key={s.symbol} className="rounded-lg p-3" style={{ background: '#0b1220', border: `1px solid ${neg ? 'rgba(239,68,68,0.4)' : '#1f2937'}` }}>
                      <p className="text-sm font-semibold text-slate-200">{s.symbol}</p>
                      <p className={`text-sm mt-0.5 ${neg ? 'text-red-300' : 'text-emerald-300'}`} style={mono}>{pct(s.expectancy)}</p>
                      <p className="text-[11px] text-slate-500" style={mono}>n={s.n ?? '-'}</p>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="overflow-x-auto">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Waves (in-sample choice, then blind out-of-sample)</p>
              <table className="w-full text-xs" style={mono}>
                <thead>
                  <tr className="text-slate-500 text-left">
                    <th className="pr-3 py-1">wave</th><th className="pr-3">in-sample window</th><th className="pr-3">out-of-sample window</th>
                    <th className="pr-3">best combination</th><th className="pr-3 text-right">IS n</th><th className="pr-3 text-right">IS exp</th>
                    <th className="pr-3 text-right">OOS n</th><th className="text-right">OOS exp</th>
                  </tr>
                </thead>
                <tbody>
                  {view.waves.map((w) => (
                    <tr key={w.wave} className="border-t border-slate-800 text-slate-300">
                      <td className="pr-3 py-1">{w.wave}</td>
                      <td className="pr-3">{w.is_start}..{w.is_end}</td>
                      <td className="pr-3">{w.oos_start}..{w.oos_end}</td>
                      <td className="pr-3">{w.best}</td>
                      <td className="pr-3 text-right">{w.is_n}</td>
                      <td className="pr-3 text-right">{pct(w.is_expectancy, 3)}</td>
                      <td className="pr-3 text-right">{w.oos_n}</td>
                      <td className={`text-right ${Number(w.oos_expectancy) < 0 ? 'text-red-300' : ''}`}>{pct(w.oos_expectancy, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {view.header && <p className="text-[11px] text-slate-500">{view.header}</p>}
            {view.note && <p className="text-[11px] text-slate-500">{view.note}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
